/**
 * Reactions Service
 * API calls for emoji reaction operations
 */

import apiClient from './api.client';
import type { ApiResponse, MessageReaction, GroupedReaction } from '@/types/api.types';

/**
 * Add a reaction to a message
 */
export const addReaction = async (
  messageId: string,
  emoji: string
): Promise<MessageReaction> => {
  const response = await apiClient.post<ApiResponse<{ reaction: MessageReaction }>>(
    `/messages/${messageId}/reactions`,
    { emoji }
  );

  if (!response.data.data) {
    throw new Error(response.data.error || 'Failed to add reaction');
  }

  return response.data.data.reaction;
};

/**
 * Remove a reaction from a message
 */
export const removeReaction = async (
  messageId: string,
  emoji: string
): Promise<void> => {
  const response = await apiClient.delete<ApiResponse>(
    `/messages/${messageId}/reactions/${encodeURIComponent(emoji)}`
  );

  if (response.data.status === 'error') {
    throw new Error(response.data.error || 'Failed to remove reaction');
  }
};

/**
 * Toggle a reaction (add if doesn't exist, remove if exists)
 */
export const toggleReaction = async (
  messageId: string,
  emoji: string
): Promise<{ action: 'added' | 'removed'; reaction?: MessageReaction }> => {
  const response = await apiClient.post<ApiResponse<{
    action: 'added' | 'removed';
    reaction?: MessageReaction;
  }>>(
    `/messages/${messageId}/reactions/toggle`,
    { emoji }
  );

  if (!response.data.data) {
    throw new Error(response.data.error || 'Failed to toggle reaction');
  }

  return response.data.data;
};

/**
 * Get all reactions for a message (grouped by emoji)
 */
export const getMessageReactions = async (
  messageId: string
): Promise<GroupedReaction[]> => {
  const response = await apiClient.get<ApiResponse<{ reactions: GroupedReaction[] }>>(
    `/messages/${messageId}/reactions`
  );

  if (!response.data.data) {
    throw new Error(response.data.error || 'Failed to get reactions');
  }

  return response.data.data.reactions;
};
